import React, { useState } from 'react';
import { FiArrowLeft, FiCalendar, FiChevronDown, FiDownload, FiBell } from 'react-icons/fi';

const SensorListHeader = () => {
  // Calendar tracking states
  const [startDate, setStartDate] = useState('2026-05-26');
  const [endDate, setEndDate] = useState('2026-06-01');
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  // Mock list of live notifications
  const activeAlerts = [
    { id: 1, type: 'Critical', msg: 'Deep Freeze A fell to -12.4°C.', time: '3 mins ago' },
    { id: 2, type: 'Warning', msg: 'Walk-In Fridge reached 5.1°C.', time: '14 mins ago' },
    { id: 3, type: 'Warning', msg: 'Prep Chill door left unlatched.', time: '1 hr ago' }
  ];

  // Helper function to turn numeric input dates into cleaner summary labels
  const formatDateLabel = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  return (
    <header className="w-full font-sans select-none flex flex-col md:flex-row md:items-end md:justify-between gap-6">
      
      {/* Left side: Navigation links and title content */}
      <div className="space-y-3">
        {/* Back Link Button */}
        <button className="flex items-center gap-2 text-[14px] font-bold text-[#0052FF] hover:opacity-80 transition-opacity cursor-pointer">
          <FiArrowLeft className="w-4 h-4 stroke-[2.5]" />
          <span>Back to Dashboard</span>
        </button>
        
        {/* Page Headings */}
        <div className="space-y-1">
          <h1 className="text-[24px] font-bold text-gray-900 tracking-tight">
            Stage 2 – Sensor List (Overview – All Sensors)
          </h1>
          <p className="text-[14px] font-medium text-gray-400">
            Detailed list of all temperature sensors with live readings and status.
          </p>
        </div>
      </div>

      {/* Right side: Action dropdown selectors and utility buttons */}
      <div className="flex items-center gap-3 shrink-0 self-start md:self-end">
        
        {/* Interactive Custom Calendar Date Selector */}
        <div className="relative">
          <button 
            onClick={() => setIsCalendarOpen(!isCalendarOpen)}
            className="flex items-center gap-3 bg-white border border-gray-200 hover:border-gray-300 p-3 px-4 rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.01)] transition-all text-gray-700 cursor-pointer"
          >
            <FiCalendar className="w-4 h-4 text-gray-800" />
            <span className="text-[14px] font-bold tracking-tight text-gray-800">
              {formatDateLabel(startDate)} – {formatDateLabel(endDate)}
            </span>
            <FiChevronDown className={`w-4 h-4 text-gray-500 ml-1 stroke-[2.5] transition-transform ${isCalendarOpen ? 'rotate-180' : ''}`} />
          </button>

          {/* Absolute Custom Calendar Picker Panel */}
          {isCalendarOpen && (
            <div className="absolute right-0 mt-2 bg-white border border-gray-100 rounded-xl shadow-xl z-50 p-4 w-72 animate-in fade-in slide-in-from-top-2 duration-150">
              <h4 className="text-[13px] font-bold text-gray-900 mb-3">Select Custom Range</h4>
              <div className="space-y-3">
                {/* Start Date Selection */}
                <div>
                  <label className="text-[11px] font-bold text-gray-400 block mb-1">START DATE</label>
                  <input 
                    type="date" 
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg p-2 text-sm font-semibold text-gray-800 focus:outline-none focus:border-blue-500"
                  />
                </div>
                {/* End Date Selection */}
                <div>
                  <label className="text-[11px] font-bold text-gray-400 block mb-1">END DATE</label>
                  <input 
                    type="date" 
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg p-2 text-sm font-semibold text-gray-800 focus:outline-none focus:border-blue-500"
                  />
                </div>
                {/* Apply Closure Trigger Button */}
                <button 
                  onClick={() => setIsCalendarOpen(false)}
                  className="w-full mt-2 py-2 bg-[#0052FF] hover:bg-blue-700 text-white font-bold text-xs rounded-lg shadow-xs transition-colors cursor-pointer"
                >
                  Apply Custom Range
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Small Export Utility Button */}
        <button className="flex items-center gap-2 bg-white border border-gray-200 hover:border-gray-300 p-3 px-4 rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.01)] transition-all cursor-pointer">
          <FiDownload className="w-4 h-4 text-[#0052FF] stroke-[2.2]" />
          <span className="text-[14px] font-bold text-[#0052FF] tracking-tight">
            Export Report
          </span>
        </button>

        {/* 
          Hover Notification Container using Tailwind's 'group' selector.
          Hovering over this outer div displays the hidden absolute popup panel automatically.
        */}
        <div className="relative group py-2">
          
          {/* Rounded Notification Button Wrapper */}
          <button className="relative bg-white border border-gray-200 group-hover:border-gray-300 w-11 h-11 rounded-xl flex items-center justify-center shadow-[0_2px_8px_rgba(0,0,0,0.01)] transition-all text-gray-700">
            <FiBell className="w-5 h-5" />
            <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[11px] font-bold w-5 h-5 rounded-full flex items-center justify-center border-2 border-white shadow-sm">
              {activeAlerts.length}
            </span>
          </button>

          {/* 
            Absolute Hover Notification Dropdown Panel.
            Hidden by default ('hidden'), then displayed via ('group-hover:block').
          */}
          <div className="hidden group-hover:block absolute right-0 top-12 w-80 bg-white border border-gray-100 rounded-xl shadow-xl z-50 py-2 animate-in fade-in duration-100">
            
            {/* Header Title inside Dropdown Menu */}
            <div className="px-4 py-2 border-b border-gray-50 flex items-center justify-between">
              <span className="text-xs font-bold text-gray-900">Recent Notifications</span>
              <span className="text-[10px] bg-red-50 text-red-600 font-extrabold px-1.5 py-0.5 rounded-full">
                Live
              </span>
            </div>

            {/* List Body Container */}
            <div className="max-h-64 overflow-y-auto divide-y divide-gray-50">
              {activeAlerts.map((alert) => (
                <div key={alert.id} className="p-3.5 hover:bg-gray-50/50 transition-colors flex items-start gap-3">
                  <span className={`text-[9px] font-extrabold uppercase px-1.5 py-0.5 rounded-md mt-0.5 shrink-0 ${
                    alert.type === 'Critical' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                  }`}>
                    {alert.type}
                  </span>
                  <div className="space-y-0.5">
                    <p className="text-[12px] font-semibold text-gray-700 leading-snug">
                      {alert.msg}
                    </p>
                    <span className="text-[10px] font-medium text-gray-400 block">
                      {alert.time}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Bottom Link Inside Dropdown Menu */}
            <div className="px-3 pt-2 pb-1 border-t border-gray-50 text-center">
              <button className="text-[11px] font-bold text-[#0052FF] hover:underline w-full">
                View All Notification Logs
              </button>
            </div>

          </div>
        </div>

      </div>

    </header>
  );
};

export default SensorListHeader;
